// src/services/mockUnclaimedContentService.ts

import { 
  UnclaimedContent, 
  UnclaimedContentStats,
  ApiResponse 
} from '@/types/unclaimed-content';

// Mock data generator
const generateMockContent = (id: number): UnclaimedContent => ({
  documentId: `mock_content_${id}`,
  id: id,
  attributes: {
    title: `Tác phẩm #${id} - ${['Bài hát hay', 'Hình ảnh đẹp', 'Video clip', 'Văn bản', 'Tranh vẽ'][id % 5]}`,
    description: `Mô tả chi tiết cho tác phẩm số ${id}. Đây là một tác phẩm được phát hiện trên internet nhưng chưa có thông tin tác giả rõ ràng.`,
    contentType: ['music', 'image', 'video', 'text', 'artwork'][id % 5] as any,
    thumbnailUrl: id % 3 === 0 ? `https://picsum.photos/300/200?random=${id}` : undefined,
    source: ['vietrro_vault', 'orphan_internet', 'partner_platform'][id % 3] as any,
    discoveryMethod: ['web_crawl', 'user_report', 'ai_detection', 'vault_import'][id % 4] as any,
    discoverySource: 'Mock Discovery System',
    sourceMetadata: {
      originalUrl: `https://example.com/content/${id}`,
      crawledAt: new Date().toISOString()
    },
    potentialRevenue: Math.floor(Math.random() * 10000000) + 100000,
    contentStatus: ['available', 'claimed', 'disputed', 'verified'][id % 4] as any,
    discoveredAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastUpdatedAt: new Date().toISOString(),
    tags: [`tag${id}`, 'vietnam', 'creative'],
    category: 'entertainment',
    claim_requests: id % 3 === 0 ? [{
      documentId: `claim_${id}`,
      id: id * 100,
      attributes: {
        claimantInfo: {
          name: `Người claim ${id}`,
          email: `claimant${id}@example.com`,
          relationship: 'original_author' as any
        },
        claimType: 'original_author' as any,
        claimStatus: ['pending', 'approved', 'rejected'][id % 3] as any,
        statement: `Tôi là tác giả của tác phẩm này...`,
        submittedAt: new Date().toISOString(),
        priority: 'normal' as any,
        reviewStage: 'initial' as any
      }
    }] : []
  }
});

const generateMockStats = (): UnclaimedContentStats => ({
  totalContent: 1247,
  availableContent: 856,
  claimedContent: 245,
  disputedContent: 89,
  totalPotentialRevenue: 2840000000,
  averageContentValue: 2280000,
  highValueContentCount: 156,
  totalClaims: 423,
  pendingClaims: 78,
  successfulClaims: 267,
  averageProcessingTime: 18,
  contentBySource: {
    vietrro_vault: { count: 542, totalValue: 1200000000, avgValue: 2214000 },
    orphan_internet: { count: 489, totalValue: 980000000, avgValue: 2004000 },
    partner_platform: { count: 216, totalValue: 660000000, avgValue: 3055000 }
  },
  contentByType: {
    music: { count: 345, claimRate: 38.5, successRate: 72.1 },
    image: { count: 298, claimRate: 28.2, successRate: 68.9 },
    video: { count: 234, claimRate: 45.7, successRate: 75.3 },
    text: { count: 198, claimRate: 22.1, successRate: 65.4 },
    artwork: { count: 172, claimRate: 41.3, successRate: 71.8 }
  },
  recentlyAdded: 45,
  recentClaims: 23,
  growthRate: 15.2,
  claimTrends: [
    { date: '2025-01-20', claims: 12, approvals: 8 },
    { date: '2025-01-21', claims: 15, approvals: 11 },
    { date: '2025-01-22', claims: 18, approvals: 13 },
    { date: '2025-01-23', claims: 14, approvals: 9 },
    { date: '2025-01-24', claims: 16, approvals: 12 }
  ]
});

// Mock service implementation
export class MockUnclaimedContentService {
  private mockContents: UnclaimedContent[] = [];
  private mockStats: UnclaimedContentStats;

  constructor() {
    // Generate mock data
    this.mockContents = Array.from({ length: 50 }, (_, i) => generateMockContent(i + 1));
    this.mockStats = generateMockStats();
  }

  async getUnclaimedContents(params: any = {}): Promise<ApiResponse<UnclaimedContent[]>> {
    await this.delay(500); // Simulate network delay

    const { page = 1, pageSize = 20, filters = {} } = params;
    let filteredContents = [...this.mockContents];

    // Apply filters
    if (filters.sources?.length) {
      filteredContents = filteredContents.filter(c => 
        filters.sources.includes(c.source)
      );
    }

    if (filters.contentTypes?.length) {
      filteredContents = filteredContents.filter(c => 
        filters.contentTypes.includes(c.contentType)
      );
    }

    if (filters.status?.length) {
      filteredContents = filteredContents.filter(c => 
        filters.status.includes(c.contentStatus)
      );
    }

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filteredContents = filteredContents.filter(c => 
        c.title.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query)
      );
    }

    if (filters.potentialRevenueRange?.min) {
      filteredContents = filteredContents.filter(c => 
        c.potentialRevenue >= filters.potentialRevenueRange.min
      );
    }

    if (filters.potentialRevenueRange?.max) {
      filteredContents = filteredContents.filter(c => 
        c.potentialRevenue <= filters.potentialRevenueRange.max
      );
    }

    // Apply sorting
    const sortBy = filters.sortBy || 'potentialRevenue';
    const sortOrder = filters.sortOrder || 'desc';
    
    filteredContents.sort((a, b) => {
      let aVal = a.attributes[sortBy as keyof typeof a.attributes];
      let bVal = b.attributes[sortBy as keyof typeof b.attributes];
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    // Apply pagination
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedContents = filteredContents.slice(startIndex, endIndex);

    return {
      data: paginatedContents,
      meta: {
        pagination: {
          page,
          pageSize,
          pageCount: Math.ceil(filteredContents.length / pageSize),
          total: filteredContents.length
        }
      }
    };
  }

  async getUnclaimedContentById(id: string): Promise<ApiResponse<UnclaimedContent>> {
    await this.delay(300);
    
    const content = this.mockContents.find(c => c.documentId === id);
    if (!content) {
      throw new Error('Content not found');
    }

    return {
      data: content,
      meta: {}
    };
  }

  async getStats(filters?: any): Promise<ApiResponse<UnclaimedContentStats>> {
    await this.delay(400);
    
    return {
      data: this.mockStats,
      meta: {}
    };
  }

  async submitClaimRequest(contentId: string, claimData: any): Promise<ApiResponse<any>> {
    await this.delay(1000);
    
    // Simulate claim request submission
    const mockClaim = {
      documentId: `claim_${Date.now()}`,
      id: Date.now(),
      attributes: {
        claimantInfo: claimData.claimantInfo,
        claimType: claimData.claimantInfo.relationship,
        claimStatus: 'pending',
        statement: claimData.statement,
        submittedAt: new Date().toISOString(),
        priority: 'normal',
        reviewStage: 'initial',
        unclaimed_content: { documentId: contentId }
      }
    };

    // Update mock content with new claim
    const content = this.mockContents.find(c => c.documentId === contentId);
    if (content) {
      if (!content.claim_requests) {
        content.claim_requests = [];
      }
      content.claim_requests.push(mockClaim as any);
    }

    return {
      data: mockClaim,
      meta: {}
    };
  }

  async searchContent(query: string): Promise<UnclaimedContent[]> {
    await this.delay(600);
    
    const lowerQuery = query.toLowerCase();
    return this.mockContents.filter(content =>
      content.title.toLowerCase().includes(lowerQuery) ||
      content.description.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
  }

  async advancedSearch(searchParams: any): Promise<ApiResponse<UnclaimedContent[]>> {
    // Reuse getUnclaimedContents with search parameters
    return this.getUnclaimedContents({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 20,
      filters: {
        searchQuery: searchParams.query,
        sources: searchParams.sources,
        contentTypes: searchParams.contentTypes,
        potentialRevenueRange: searchParams.valueRange,
        dateRange: searchParams.dateRange,
        status: searchParams.claimStatus
      }
    });
  }

  // Helper method to simulate network delay
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Additional mock methods for development
  async getUserClaimRequests(userId: string): Promise<ApiResponse<any[]>> {
    await this.delay(300);
    
    return {
      data: [
        {
          documentId: `claim_user_${userId}_1`,
          attributes: {
            claimStatus: 'pending',
            submittedAt: new Date().toISOString(),
            unclaimed_content: this.mockContents[0]
          }
        }
      ],
      meta: {}
    };
  }

  async createEvidenceItem(claimRequestId: string, evidenceData: any): Promise<ApiResponse<any>> {
    await this.delay(800);
    
    return {
      data: {
        documentId: `evidence_${Date.now()}`,
        attributes: {
          evidenceType: evidenceData.type,
          title: evidenceData.title,
          description: evidenceData.description,
          uploadedAt: new Date().toISOString(),
          verificationStatus: 'pending'
        }
      },
      meta: {}
    };
  }
}

// Export both mock and conditional service
export const mockUnclaimedContentService = new MockUnclaimedContentService();

// Helper to switch between mock and real service
export const getUnclaimedContentService = async () => {
  const useMock = import.meta.env.MODE === 'development' && 
                 import.meta.env.VITE_USE_MOCK_API === 'true';
  
  if (useMock) {
    console.log('🔧 Using Mock Unclaimed Content Service');
    return mockUnclaimedContentService;
  }
  
  // Return the real service using dynamic import
  try {
    const { unclaimedContentService } = await import('./unclaimedContentService');
    return unclaimedContentService;
  } catch (error) {
    console.warn('⚠️ Real service not available, falling back to mock service');
    return mockUnclaimedContentService;
  }
};